import { CardDescription } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { EventDescriptionEditor } from "./EventDescriptionEditor";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImageUploadCropper } from "@/components/ui/image-upload-cropper";
import { useState } from "react";
import { EventStatus } from "@satyrsmc/shared/lib/enums";
import { isBadgerEvent, isCampoutEvent, isRideEvent } from "@/lib/event-constants";

const POSTER_RATIOS = [
  { value: "1:1", label: "Square (1:1)", ratio: 1 },
  { value: "1.91:1", label: "Landscape (1.91:1)", ratio: 1.91 },
  { value: "4:5", label: "Portrait (4:5)", ratio: 4 / 5 },
  { value: "9:16", label: "Reel (9:16)", ratio: 9 / 16 },
] as const;

type PosterRatioValue = (typeof POSTER_RATIOS)[number]["value"];

interface BudgetSummary {
  id: string;
  name: string;
  year: number;
}

interface ScenarioSummary {
  id: string;
  name: string;
}

interface EditEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editName: string;
  setEditName: (v: string) => void;
  editDescription: string;
  setEditDescription: (v: string) => void;
  editYear: number | "";
  setEditYear: (v: number | "") => void;
  editStartDate: string;
  setEditStartDate: (v: string) => void;
  editEndDate: string;
  setEditEndDate: (v: string) => void;
  editRefundCutoffDate: string;
  setEditRefundCutoffDate: (v: string) => void;
  editEventUrl: string;
  setEditEventUrl: (v: string) => void;
  editEventUrlText: string;
  setEditEventUrlText: (v: string) => void;
  editEventLocation: string;
  setEditEventLocation: (v: string) => void;
  editEventLocationLink: string;
  setEditEventLocationLink: (v: string) => void;
  editEventLocationEmbed: string;
  setEditEventLocationEmbed: (v: string) => void;
  editGaTicketCost: string;
  setEditGaTicketCost: (v: string) => void;
  editDayPassCost: string;
  setEditDayPassCost: (v: string) => void;
  editMemberCost: string;
  setEditMemberCost: (v: string) => void;
  editGaTicketsSold: string;
  setEditGaTicketsSold: (v: string) => void;
  editDayPassesSold: string;
  setEditDayPassesSold: (v: string) => void;
  editBudgetId: string;
  setEditBudgetId: (v: string) => void;
  editScenarioId: string;
  setEditScenarioId: (v: string) => void;
  editPlanningNotes: string;
  setEditPlanningNotes: (v: string) => void;
  editEventType: string;
  setEditEventType: (v: string) => void;
  editStartLocation: string;
  setEditStartLocation: (v: string) => void;
  editEndLocation: string;
  setEditEndLocation: (v: string) => void;
  editFacebookEventUrl: string;
  setEditFacebookEventUrl: (v: string) => void;
  editPreRideEventId: string;
  setEditPreRideEventId: (v: string) => void;
  editRideCost: string;
  setEditRideCost: (v: string) => void;
  editShowOnWebsite: boolean;
  setEditShowOnWebsite: (v: boolean) => void;
  editMembersOnly: boolean;
  setEditMembersOnly: (v: boolean) => void;
  editStatus: EventStatus;
  setEditStatus: (v: EventStatus) => void;
  /** Existing poster URL shown as preview (null = none set). */
  existingPosterUrl: string | null;
  /** New base64 poster chosen this session (null = remove, undefined = no change). */
  editPosterBase64: string | null | undefined;
  setEditPosterBase64: (v: string | null | undefined) => void;
  budgets: BudgetSummary[];
  scenarios: ScenarioSummary[];
  onSave: () => Promise<void>;
}

export function EditEventDialog({
  open,
  onOpenChange,
  editName,
  setEditName,
  editDescription,
  setEditDescription,
  editYear,
  setEditYear,
  editStartDate,
  setEditStartDate,
  editEndDate,
  setEditEndDate,
  editRefundCutoffDate,
  setEditRefundCutoffDate,
  editEventUrl,
  setEditEventUrl,
  editEventUrlText,
  setEditEventUrlText,
  editEventLocation,
  setEditEventLocation,
  editEventLocationLink,
  setEditEventLocationLink,
  editEventLocationEmbed,
  setEditEventLocationEmbed,
  editGaTicketCost,
  setEditGaTicketCost,
  editDayPassCost,
  setEditDayPassCost,
  editMemberCost,
  setEditMemberCost,
  editGaTicketsSold,
  setEditGaTicketsSold,
  editDayPassesSold,
  setEditDayPassesSold,
  editBudgetId,
  setEditBudgetId,
  editScenarioId,
  setEditScenarioId,
  editPlanningNotes,
  setEditPlanningNotes,
  editEventType,
  setEditEventType,
  editStartLocation,
  setEditStartLocation,
  editEndLocation,
  setEditEndLocation,
  editFacebookEventUrl,
  setEditFacebookEventUrl,
  editPreRideEventId,
  setEditPreRideEventId,
  editRideCost,
  setEditRideCost,
  editShowOnWebsite,
  setEditShowOnWebsite,
  editMembersOnly,
  setEditMembersOnly,
  editStatus,
  setEditStatus,
  existingPosterUrl,
  editPosterBase64,
  setEditPosterBase64,
  budgets,
  scenarios,
  onSave,
}: EditEventDialogProps) {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [posterRatio, setPosterRatio] = useState<PosterRatioValue>("4:5");

  const handleSave = async () => {
    await onSave();
    onOpenChange(false);
  };

  // What to show as the current poster state:
  // - editPosterBase64 = string → new image chosen this session
  // - editPosterBase64 = null → user explicitly removed it
  // - editPosterBase64 = undefined → unchanged; show existingPosterUrl
  const hasPoster =
    editPosterBase64 !== null && (editPosterBase64 !== undefined || existingPosterUrl !== null);
  const activeCropRatio = POSTER_RATIOS.find((r) => r.value === posterRatio)!;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto overflow-x-hidden max-w-3xl w-[calc(100vw-2rem)]">
          <DialogHeader>
            <DialogTitle>Edit Event</DialogTitle>
            <CardDescription>Update event details</CardDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 min-w-0 overflow-hidden">
            <div className="grid gap-4 sm:grid-cols-2 min-w-0">
              <div className="space-y-2 min-w-0">
                <Label>Name</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Event name"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Event type</Label>
                <Select value={editEventType} onValueChange={setEditEventType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="badger">Badger</SelectItem>
                    <SelectItem value="social">Social</SelectItem>
                    <SelectItem value="pioneer_run">Pioneer Run</SelectItem>
                    <SelectItem value="rides">Rides</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 min-w-0">
              <div className="space-y-2 min-w-0">
                <Label>Year</Label>
                <Input
                  type="number"
                  value={editYear}
                  onChange={(e) =>
                    setEditYear(e.target.value === "" ? "" : parseInt(e.target.value, 10))
                  }
                  placeholder="2025"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Start Date</Label>
                <DatePicker value={editStartDate} onChange={setEditStartDate} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 min-w-0">
              <div className="space-y-2 min-w-0">
                <Label>End Date</Label>
                <DatePicker value={editEndDate} onChange={setEditEndDate} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <EventDescriptionEditor
                value={editDescription}
                onChange={setEditDescription}
                placeholder="Write a description..."
              />
            </div>
            <div className="space-y-2 rounded-md border border-border p-3 min-w-0">
              <p className="text-sm font-medium">Event URL</p>
              <div className="space-y-2 min-w-0">
                <Label className="text-xs text-muted-foreground">URL</Label>
                <Input
                  value={editEventUrl}
                  onChange={(e) => setEditEventUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label className="text-xs text-muted-foreground">Button Text</Label>
                <Input
                  value={editEventUrlText}
                  onChange={(e) => setEditEventUrlText(e.target.value)}
                  placeholder="Click here"
                />
              </div>
            </div>
            {isRideEvent(editEventType) && (
              <div className="space-y-4 rounded-lg border p-4">
                <p className="text-sm font-medium">Ride details</p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Start location</Label>
                    <Input
                      value={editStartLocation}
                      onChange={(e) => setEditStartLocation(e.target.value)}
                      placeholder="Meet location"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End location</Label>
                    <Input
                      value={editEndLocation}
                      onChange={(e) => setEditEndLocation(e.target.value)}
                      placeholder="Final destination"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Facebook event URL</Label>
                  <Input
                    value={editFacebookEventUrl}
                    onChange={(e) => setEditFacebookEventUrl(e.target.value)}
                    placeholder="https://facebook.com/events/..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Ride cost ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editRideCost}
                    onChange={(e) => setEditRideCost(e.target.value)}
                    placeholder="0 or leave empty for free"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pre-ride event ID</Label>
                  <Input
                    value={editPreRideEventId}
                    onChange={(e) => setEditPreRideEventId(e.target.value)}
                    placeholder="Event ID of pre-ride (optional)"
                  />
                </div>
              </div>
            )}
            <div className="space-y-2 rounded-md border border-border p-3 min-w-0">
              <p className="text-sm font-medium">Location</p>
              <div className="space-y-2 min-w-0">
                <Label className="text-xs text-muted-foreground">
                  Address or venue name (optional)
                </Label>
                <Input
                  value={editEventLocation}
                  onChange={(e) => setEditEventLocation(e.target.value)}
                  placeholder="Camp Mather, Groveland CA"
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label className="text-xs text-muted-foreground">
                  Google Maps link (optional, disabled when address is set)
                </Label>
                <Input
                  value={editEventLocationLink}
                  onChange={(e) => setEditEventLocationLink(e.target.value)}
                  placeholder="https://maps.google.com/..."
                  disabled={!!editEventLocation.trim()}
                />
              </div>
              <div className="space-y-2 min-w-0">
                <Label className="text-xs text-muted-foreground">Map Embed (optional)</Label>
                <Textarea
                  value={editEventLocationEmbed}
                  onChange={(e) => setEditEventLocationEmbed(e.target.value)}
                  placeholder="Paste the iframe HTML from Google Maps (Share → Embed a map)."
                  rows={3}
                  className="font-mono text-sm w-full min-w-0 overflow-x-auto"
                />
              </div>
            </div>
            {isCampoutEvent(editEventType) && (
              <div className="space-y-4 rounded-lg border p-4">
                <p className="text-sm font-medium">Campout details</p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label>GA Ticket Cost ($)</Label>
                    <Input
                      type="number"
                      value={editGaTicketCost}
                      onChange={(e) => setEditGaTicketCost(e.target.value)}
                      placeholder="200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Day Pass Cost ($)</Label>
                    <Input
                      type="number"
                      value={editDayPassCost}
                      onChange={(e) => setEditDayPassCost(e.target.value)}
                      placeholder="50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Member Cost ($)</Label>
                    <Input
                      type="number"
                      value={editMemberCost}
                      onChange={(e) => setEditMemberCost(e.target.value)}
                      placeholder="150"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>GA Tickets Sold</Label>
                    <Input
                      type="number"
                      value={editGaTicketsSold}
                      onChange={(e) => setEditGaTicketsSold(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Day Passes Sold</Label>
                    <Input
                      type="number"
                      value={editDayPassesSold}
                      onChange={(e) => setEditDayPassesSold(e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Budget</Label>
                    <Select
                      value={editBudgetId || "none"}
                      onValueChange={(v) => setEditBudgetId(v === "none" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select budget" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {budgets.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name} ({b.year})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Scenario</Label>
                    <Select
                      value={editScenarioId || "none"}
                      onValueChange={(v) => setEditScenarioId(v === "none" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select scenario" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {scenarios.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
            {isBadgerEvent(editEventType) && (
              <div className="space-y-2 rounded-lg border p-4">
                <Label>Refund Cutoff Date</Label>
                <DatePicker value={editRefundCutoffDate} onChange={setEditRefundCutoffDate} />
                <p className="text-xs text-muted-foreground">
                  Members can no longer cancel their RSVP after this date.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Planning Notes</Label>
              <Textarea
                value={editPlanningNotes}
                onChange={(e) => setEditPlanningNotes(e.target.value)}
                placeholder="Free-form notes..."
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label>Event status</Label>
              <Select value={editStatus} onValueChange={(v) => setEditStatus(v as EventStatus)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EventStatus.Draft}>Draft</SelectItem>
                  <SelectItem value={EventStatus.Public}>Public</SelectItem>
                  <SelectItem value={EventStatus.Cancelled}>Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Event poster</Label>
              {hasPoster && (
                <div className="mb-2 text-sm text-muted-foreground">
                  {editPosterBase64
                    ? "New poster selected — will be uploaded on save."
                    : "Existing poster set."}
                </div>
              )}
              <div className="flex gap-2">
                <Select
                  value={posterRatio}
                  onValueChange={(v) => setPosterRatio(v as PosterRatioValue)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {POSTER_RATIOS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant={editPosterBase64 ? "default" : "outline"}
                  onClick={() => setCropperOpen(true)}
                >
                  {editPosterBase64 ? "Change" : hasPoster ? "Replace" : "Upload"}
                </Button>
                {hasPoster && (
                  <Button type="button" variant="ghost" onClick={() => setEditPosterBase64(null)}>
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editShowOnWebsite}
                onChange={(e) => setEditShowOnWebsite(e.target.checked)}
                className="size-4 rounded border-input"
              />
              <span className="text-sm font-medium">Show on website</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={editMembersOnly}
                onChange={(e) => setEditMembersOnly(e.target.checked)}
                className="size-4 rounded border-input"
              />
              <span className="text-sm font-medium">Members only</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImageUploadCropper
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        onConfirm={(base64) => setEditPosterBase64(base64)}
        aspectRatio={activeCropRatio.ratio}
        title={`Event Poster — ${activeCropRatio.label}`}
      />
    </>
  );
}
